// Vista local = último paquete del servidor + operaciones que el servidor todavía no confirmó (documento 06, F10).
// Funciones puras: se prueban sin navegador. Reproducen lo que hará la API con las mismas funciones de @reparto/shared,
// así lo que ve el chofer sin señal coincide con lo que queda guardado al sincronizar.
import {
  calcularRendicion,
  type Configuracion,
  deMinutos,
  enlacesGoogleMaps,
  formatoHora,
  haversineKm,
  hoyISO,
  huellaCarga,
  metricasRuta,
  montoEsperado,
  optimizarOrden,
  type ParadaParaRuta,
  type PayloadOperacion,
  type Punto,
  resultadoPorBultos,
} from '@reparto/shared/nucleo';
import type { OperacionLocal } from './db';
import type { Paquete, Parada, PedidoCarga, Rendicion, Ruta, Trazado } from './tipos';

export const parametros = (c: Configuracion) => ({
  factorCalle: c.ruta.factor_distancia_calle,
  velocidadKmh: c.ruta.velocidad_media_kmh,
  minutosPorParada: c.ruta.minutos_por_parada,
});

const clonar = <T>(x: T): T => structuredClone(x);

const entre = (min: number, x: number, max: number) => Math.min(max, Math.max(min, x));

/** Dónde para el móvil: la ubicación verificada, el centro de la zona o, sin nada, el depósito. */
export const destinoParada = (p: Parada, deposito: Punto): Punto =>
  p.cliente.ubicacion ?? p.cliente.ubicacion_aproximada ?? deposito;

/** El trazado por calle del servidor, solo si es para el orden actual (si el chofer reordenó sin señal, no sirve). */
export function trazadoVigente(ruta: Pick<Ruta, 'trazado' | 'paradas'>): Trazado | null {
  const t = ruta.trazado;
  if (!t || t.puntos.length < 2 || t.orden.length !== ruta.paradas.length) return null;
  return ruta.paradas.every((p, i) => p.id === t.orden[i]) ? t : null;
}

/**
 * Parámetros calibrados con el último trazado por calle del servidor: cuánto más larga que la recta es la ruta de
 * verdad y a qué velocidad se maneja en esa zona. Así las llegadas estimadas sin señal se parecen a las del servidor
 * (que usa OSRM) aunque el chofer cambie el orden. Sin trazado, los de la configuración.
 */
export function parametrosRuta(ruta: Ruta, c: Configuracion) {
  const base = parametros(c);
  const t = ruta.trazado;
  if (!t || t.km <= 0 || t.min <= 0) return base;
  const porId = new Map(ruta.paradas.map((p) => [p.id, p]));
  const dep = ruta.deposito.ubicacion;
  const puntos = [dep, ...t.orden.map((id) => porId.get(id)).map((p) => (p ? destinoParada(p, dep) : null)), dep];
  if (puntos.some((p) => !p)) return base;
  const recta = puntos.slice(1).reduce((s, p, i) => s + haversineKm(puntos[i]!, p!), 0);
  if (recta < 0.5) return base;
  return {
    ...base,
    factorCalle: entre(1, t.km / recta, 3),
    velocidadKmh: entre(10, t.km / (t.min / 60), 90),
  };
}

export function paradaParaRuta(p: Parada): ParadaParaRuta {
  return {
    id: p.id,
    ubicacion: p.cliente.ubicacion,
    ubicacionAproximada: p.cliente.ubicacion_aproximada,
    zonaId: p.cliente.zona_id,
    ventanaDesde: p.pedido.ventana_desde,
    ventanaHasta: p.pedido.ventana_hasta,
    fijada: p.resultado !== 'pendiente',
  };
}

/** Hora de salida para estimar llegadas, con la misma regla que la API: la real, ahora si es hoy, o 08:00. */
export function horaSalidaEstimada(ruta: Pick<Ruta, 'fecha' | 'salida_en'>, ahora: Date = new Date()): string {
  const base = ruta.salida_en ? new Date(ruta.salida_en) : ruta.fecha === hoyISO(undefined, ahora) ? ahora : null;
  return base ? formatoHora(base) : '08:00';
}

const aMinutos = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** Recalcula kilómetros, minutos, llegadas estimadas y enlaces con el orden actual. */
export function recalcular(ruta: Ruta, config: Configuracion, ahora: Date = new Date()): Ruta {
  ruta.paradas.forEach((p, i) => (p.orden = i + 1));
  const salida = horaSalidaEstimada(ruta, ahora);
  const m = metricasRuta(ruta.deposito.ubicacion, ruta.paradas.map(paradaParaRuta), parametrosRuta(ruta, config), {
    horaSalida: salida,
  });
  const llegadas = new Map(m.llegadas.map((l) => [l.id, l]));
  for (const p of ruta.paradas) {
    const l = llegadas.get(p.id);
    p.llegada_estimada = l?.llegada || null;
    p.ventana_en_riesgo = p.resultado === 'pendiente' ? (l?.fueraDeVentana ?? false) : false;
  }
  ruta.km_estimados = m.km;
  ruta.min_estimados = m.minutos;
  ruta.enlaces_maps = enlacesGoogleMaps(
    ruta.deposito.ubicacion,
    ruta.paradas.map((p) => p.cliente.ubicacion ?? p.cliente.ubicacion_aproximada ?? ruta.deposito.ubicacion),
  );
  ruta.resumen = {
    paradas: ruta.paradas.length,
    resueltas: ruta.paradas.filter((p) => p.resultado !== 'pendiente').length,
    bultos: ruta.paradas.reduce((s, p) => s + p.pedido.bultos_total, 0),
    cobrado: ruta.paradas.reduce((s, p) => s + (p.cobro?.monto_cobrado ?? 0), 0),
    hora_estimada_fin: deMinutos(aMinutos(salida) + m.minutos),
  };
  return ruta;
}

function rutaDesdeCarga(paq: Paquete, op: PayloadOperacion<'confirmar_carga'>, hora: string): Ruta | null {
  if (!paq.carga || !paq.deposito || !op.ruta_id) return null;
  const ids = new Map((op.paradas ?? []).map((p) => [p.pedido_id, p.parada_id]));
  const tildados = paq.carga.datos
    .filter((p) => p.cargado && p.estado === 'asignado' && ids.has(p.id))
    .sort((a, b) => (marca(a) ?? '').localeCompare(marca(b) ?? '') || a.numero - b.numero);
  if (!tildados.length) return null;
  const paradas: Parada[] = tildados.map((p, i) => ({
    id: ids.get(p.id)!,
    orden: i + 1,
    resultado: 'pendiente',
    motivo: null,
    motivo_texto: null,
    llegada_en: null,
    resuelta_en: null,
    comprobante: null,
    observaciones: null,
    llegada_estimada: null,
    ventana_en_riesgo: false,
    pedido: {
      id: p.id,
      numero: p.numero,
      numero_visible: p.numero_visible,
      estado: 'cargado',
      cobro: p.cobro,
      monto: p.monto,
      bultos_total: p.bultos_total,
      ventana_desde: p.ventana_desde,
      ventana_hasta: p.ventana_hasta,
      observaciones: p.observaciones,
    },
    cliente: clonar(p.cliente),
    items: p.items.map((i) => ({ ...i, bultos_entregados: null, bultos_devueltos: null })),
    cobro: null,
    archivos: [],
    pendiente_sync: true,
  }));
  for (const p of paq.carga.datos) {
    if (ids.has(p.id) && p.cargado) p.estado = 'cargado';
  }
  return {
    id: op.ruta_id,
    fecha: paq.fecha,
    estado: 'armada',
    optimizada: false,
    km_estimados: null,
    min_estimados: null,
    km_antes_optimizar: null,
    km_recorridos: null,
    carga_confirmada_en: hora,
    salida_en: null,
    cierre_en: null,
    movil: paq.movil,
    chofer: null,
    deposito: paq.deposito,
    resumen: { paradas: 0, resueltas: 0, bultos: 0, cobrado: 0 },
    paradas,
    enlaces_maps: [],
    rendicion: null,
  };
}

const marca = (p: PedidoCarga) => p.cargado_por_chofer_en ?? p.cargado_por_encargado_en;

function rendicionLocal(ruta: Ruta, observacion: string | null): Rendicion {
  const r = calcularRendicion(
    ruta.paradas.map((p) => ({
      resultado: p.resultado,
      items: p.items.map((i) => ({ bultosCargados: i.bultos, bultosEntregados: i.bultos_entregados })),
      cobro: p.cobro
        ? {
            medio: p.cobro.medio,
            montoCobrado: p.cobro.monto_cobrado,
            montoEsperado: p.cobro.monto_esperado,
            montoACuenta: p.cobro.monto_a_cuenta,
          }
        : null,
    })),
  );
  return {
    id: 'local',
    ruta_id: ruta.id,
    estado: 'pendiente_recepcion',
    ...r,
    paradas_fallidas: r.paradas_fallidas + r.paradas_pendientes,
    observacion_chofer: observacion,
    recibida_en: null,
  };
}

/** Aplica una operación sobre el paquete (in place). Devuelve false si no tuvo efecto. */
export function aplicar(paq: Paquete, op: OperacionLocal): boolean {
  const config = paq.configuracion;
  const hora = op.hora_dispositivo;
  const ruta = paq.ruta;
  const parada = (id: unknown) => ruta?.paradas.find((p) => p.id === id);
  switch (op.tipo) {
    case 'tildar': {
      // Como el servidor (decisión D54): cualquiera de los dos tilda; tildado, sus artículos quedan controlados con lo
      // que sube ahora; destildado, sin controlar y sin marcas.
      const o = op.payload as PayloadOperacion<'tildar'>;
      const p = paq.carga?.datos.find((x) => x.id === o.pedido_id);
      if (!p || ruta || p.estado !== 'asignado' || config.carga.quien_tilda === 'encargado') return false;
      if (o.cargado) {
        if (!p.items.some((i) => i.bultos > 0)) return false;
        p.cargado = true;
        p.cargado_por_chofer_en = hora;
        p.no_cargado_motivo = null;
        p.items.forEach((i) => (i.controlado_en ??= hora));
      } else {
        p.cargado = false;
        p.cargado_por_chofer_en = null;
        p.cargado_por_encargado_en = null;
        p.controlado_por = null;
        p.items.forEach((i) => (i.controlado_en = null));
      }
      return true;
    }
    case 'cargar_item': {
      // Carga en parte: lo que sube de un artículo, con su motivo si es menos de lo pedido.
      const o = op.payload as PayloadOperacion<'cargar_item'>;
      if (ruta || config.carga.quien_tilda === 'encargado') return false;
      const p = paq.carga?.datos.find((x) => x.items.some((i) => i.id === o.pedido_item_id));
      if (!p || p.estado !== 'asignado') return false;
      const i = p.items.find((x) => x.id === o.pedido_item_id)!;
      const pedidos = i.bultos_pedidos ?? i.bultos;
      const motivo = o.motivo?.trim() || null;
      const enParte = o.bultos < pedidos;
      if (o.bultos > pedidos || (enParte && !motivo)) return false;
      i.bultos = o.bultos;
      i.subtotal = i.bultos * i.precio_bulto;
      i.bultos_pedidos = enParte ? pedidos : null;
      i.motivo_carga = enParte ? motivo : null;
      i.controlado_en = hora;
      if (o.tildar_pedido) p.items.forEach((x) => (x.controlado_en ??= hora));
      p.bultos_total = p.items.reduce((s, x) => s + x.bultos, 0);
      if (!p.monto_manual) p.monto = p.items.reduce((s, x) => s + x.subtotal, 0);
      const todos = p.items.every((x) => x.controlado_en);
      p.cargado = todos && p.bultos_total > 0;
      if (p.cargado) {
        p.cargado_por_chofer_en = hora;
        p.no_cargado_motivo = null;
      } else {
        p.cargado_por_chofer_en = null;
        p.cargado_por_encargado_en = null;
        if (todos && p.bultos_total === 0) p.no_cargado_motivo = motivo;
      }
      return true;
    }
    case 'confirmar_carga': {
      if (ruta) return false;
      if (config.carga.quien_tilda !== 'chofer') {
        // Con el control del encargado la carga la cierra el servidor, con las dos conformidades (decisión D54): sin
        // señal queda el "Recibí conforme" guardado, y no se sale a ruta hasta que llegue y se cierre.
        if (!paq.carga) return false;
        const o = op.payload as PayloadOperacion<'confirmar_carga'>;
        for (const n of o.no_cargados ?? []) {
          const p = paq.carga.datos.find((x) => x.id === n.pedido_id && !x.cargado);
          if (p && n.motivo) p.no_cargado_motivo = n.motivo;
        }
        // Ya aplicada en el servidor, la conformidad viene en el despacho del paquete: no queda "por enviar".
        if (op.estado !== 'aplicada') paq.carga.conformidad_local = { en: hora, operacion_id: op.operacion_id };
        return true;
      }
      const nueva = rutaDesdeCarga(paq, op.payload as PayloadOperacion<'confirmar_carga'>, hora);
      if (!nueva) return false;
      paq.ruta = recalcular(nueva, config);
      if (config.ruta.arma === 'sistema') aplicar(paq, { ...op, tipo: 'optimizar', payload: { ruta_id: nueva.id } });
      return true;
    }
    case 'optimizar': {
      if (!ruta || ruta.estado === 'cerrada') return false;
      const antes = ruta.km_estimados;
      const r = optimizarOrden(
        ruta.deposito.ubicacion,
        ruta.paradas.map(paradaParaRuta),
        parametrosRuta(ruta, config),
        {
          respetarZonas: config.ruta.respetar_zonas,
        },
      );
      const porId = new Map(ruta.paradas.map((p) => [p.id, p]));
      ruta.paradas = r.orden.map((id) => porId.get(id)!).filter(Boolean);
      recalcular(ruta, config);
      ruta.optimizada = true;
      ruta.km_antes_optimizar = antes;
      return true;
    }
    case 'reordenar': {
      const o = op.payload as PayloadOperacion<'reordenar'>;
      if (!ruta || ruta.estado === 'cerrada') return false;
      const porId = new Map(ruta.paradas.map((p) => [p.id, p]));
      if (o.parada_ids.length !== ruta.paradas.length || !o.parada_ids.every((id) => porId.has(id))) return false;
      ruta.paradas = o.parada_ids.map((id) => porId.get(id)!);
      ruta.optimizada = false;
      recalcular(ruta, config);
      return true;
    }
    case 'salir': {
      if (!ruta || ruta.estado !== 'armada') return false;
      ruta.estado = 'en_curso';
      ruta.salida_en = hora;
      ruta.paradas.forEach((p) => {
        if (p.pedido.estado === 'cargado') p.pedido.estado = 'en_ruta';
      });
      return true;
    }
    case 'llegada': {
      const p = parada((op.payload as PayloadOperacion<'llegada'>).parada_id);
      if (!p) return false;
      p.llegada_en ??= hora;
      return true;
    }
    case 'entregar': {
      const o = op.payload as PayloadOperacion<'entregar'>;
      const p = parada(o.parada_id);
      if (!p || p.resultado !== 'pendiente') return false;
      const ent = new Map(o.items.map((i) => [i.pedido_item_id, i.bultos_entregados]));
      p.items.forEach((i) => {
        i.bultos_entregados = Math.min(i.bultos, ent.get(i.id) ?? 0);
        i.bultos_devueltos = i.bultos - i.bultos_entregados;
      });
      const cargados = p.items.reduce((s, i) => s + i.bultos, 0);
      const entregados = p.items.reduce((s, i) => s + (i.bultos_entregados ?? 0), 0);
      p.resultado = resultadoPorBultos(cargados, entregados);
      const esperado = montoEsperado(
        p.items.map((i) => ({ bultosEntregados: i.bultos_entregados ?? 0, precioBulto: i.precio_bulto })),
      );
      const cc = o.cobro.medio === 'cuenta_corriente';
      p.cobro = {
        medio: o.cobro.medio,
        monto_cobrado: cc ? 0 : o.cobro.monto_cobrado,
        monto_a_cuenta: cc ? esperado : 0,
        monto_esperado: esperado,
        referencia: o.cobro.referencia ?? null,
      };
      p.comprobante = o.comprobante;
      p.observaciones = o.observaciones ?? null;
      p.resuelta_en = hora;
      p.llegada_en ??= hora;
      p.pedido.estado = p.resultado === 'entregada' ? 'entregado' : 'parcial';
      p.pendiente_sync = true;
      recalcular(ruta!, config);
      return true;
    }
    case 'no_entregar': {
      const o = op.payload as PayloadOperacion<'no_entregar'>;
      const p = parada(o.parada_id);
      if (!p || p.resultado !== 'pendiente') return false;
      p.resultado = 'no_entregada';
      p.motivo = o.motivo;
      p.motivo_texto = o.motivo_texto ?? null;
      p.items.forEach((i) => {
        i.bultos_entregados = 0;
        i.bultos_devueltos = i.bultos;
      });
      p.resuelta_en = hora;
      p.pedido.estado = 'no_entregado';
      p.pendiente_sync = true;
      recalcular(ruta!, config);
      return true;
    }
    case 'cerrar': {
      const o = op.payload as PayloadOperacion<'cerrar'>;
      if (!ruta || ruta.estado !== 'en_curso') return false;
      for (const p of ruta.paradas) {
        if (p.resultado === 'pendiente') {
          p.resultado = 'no_entregada';
          p.motivo = 'sin_visitar';
          p.resuelta_en = hora;
          p.items.forEach((i) => {
            i.bultos_entregados = 0;
            i.bultos_devueltos = i.bultos;
          });
          p.pedido.estado = 'no_entregado';
        }
      }
      ruta.estado = 'cerrada';
      ruta.cierre_en = hora;
      ruta.rendicion = rendicionLocal(ruta, o.observacion_chofer ?? null);
      recalcular(ruta, config);
      return true;
    }
    case 'fijar_ubicacion_cliente':
      return false;
  }
}

/** Operaciones que todavía cuentan para la vista local. */
export function vigentes(cola: OperacionLocal[], descargadoEn: string): OperacionLocal[] {
  return cola
    .filter(
      (o) =>
        o.estado === 'pendiente' ||
        o.estado === 'enviando' ||
        (o.estado === 'aplicada' && (o.aplicada_en ?? '') > descargadoEn),
    )
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
}

export function construirVista(paquete: Paquete | null, cola: OperacionLocal[]): Paquete | null {
  if (!paquete) return null;
  const v = clonar(paquete);
  for (const op of vigentes(cola, paquete.descargado_en)) aplicar(v, op);
  if (v.ruta) recalcular(v.ruta, v.configuracion);
  return v;
}

/** Huella de la carga local (la misma cuenta que hace el servidor, decisión D54): va con el "Recibí conforme". */
export const huellaLocal = (carga: Pick<Paquete, 'carga'>['carga']): string =>
  huellaCarga(
    (carga?.datos ?? [])
      .filter((p) => p.estado === 'asignado')
      .map((p) => ({ id: p.id, cargado: p.cargado, items: p.items.map((i) => ({ id: i.id, bultos: i.bultos })) })),
  );

/** Rendición provisoria mientras la ruta no se cerró (pantalla de cierre). */
export const rendicionPrevia = (ruta: Ruta) => rendicionLocal(ruta, null);

/** Pantalla que corresponde según el estado (P1 a P6). */
export function pantallaSegun(v: Paquete | null): '/movil' | '/carga' | '/ruta/armar' | '/ruta' | '/cierre' {
  if (!v) return '/movil';
  if (!v.ruta) return '/carga';
  if (v.ruta.estado === 'armada') return '/ruta/armar';
  if (v.ruta.estado === 'en_curso') return '/ruta';
  return '/cierre';
}
